/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Organization API client — fetch and mutation helpers used by use-organization.
 */

import type { ApiClient } from "@/lib/api/api-client";
import { isApiFailure } from "@/lib/api/api-client";
import { getSupabaseToken, type ClerkGetToken } from "@/lib/auth/token-utils";
import { logger } from "@/lib/utils/logger";
import type {
  OrganizationData,
  OrganizationSettings,
  OrganizationStats,
} from "@/types";
import { transformOrganizationResponse } from "./transform";
import type { OrgApiResponse } from "./types";

const ORGANIZATION_URL = "/api/organization?includeSettings=true";
const STATS_URL = "/api/organization/stats";
const STATS_TIMEOUT_MS = 10000;

function unwrapOrgResponse(data: { data?: unknown }): OrgApiResponse | null {
  let current: unknown = data.data;

  // Peel through wrapper layers until we find an object with 'id' or 'organization_id'
  for (let depth = 0; depth < 4; depth++) {
    if (!current || typeof current !== "object") return null;

    // Found the org object
    if ("id" in current && "name" in current) {
      return current as OrgApiResponse;
    }

    // Paginated wrapper: { data: {org}, pagination: {...} }
    // or standard wrapper: { success, data, message }
    if ("data" in current) {
      current = (current as { data: unknown }).data;
      continue;
    }

    break;
  }

  return null;
}

/**
 * Fetch organization. Returns null on 401/404 (not authenticated) or 400 (no org in session yet, e.g. post-checkout); throws on other errors.
 */
export async function fetchOrganizationApi(
  api: ApiClient,
): Promise<OrganizationData | null> {
  const result = await api.get<unknown>(ORGANIZATION_URL);
  if (isApiFailure(result)) {
    if (result.status === 401 || result.status === 404) {
      logger.debug("Organization fetch skipped - user not authenticated");
      return null;
    }
    if (result.status === 400) {
      logger.debug(
        "Organization fetch skipped - no org in session (e.g. post-checkout waiting for webhook)",
      );
      return null;
    }
    throw new Error("Failed to fetch organization");
  }

  const data = result.data;
  logger.debug("Organization API response", {
    hasData: !!data,
    dataKeys:
      data && typeof data === "object" ? Object.keys(data as object) : [],
    hasNestedData: !!(data as { data?: unknown } | undefined)?.data,
  });

  const orgResponse = unwrapOrgResponse({ data });
  if (!orgResponse) {
    logger.error("Invalid organization response structure", {
      data,
      responseWrapper: (data as Record<string, unknown>)?.data,
      orgResponse,
    });
    throw new Error("Invalid organization response structure");
  }

  logger.debug("Transforming organization response", {
    hasId: !!orgResponse.id,
    hasName: !!orgResponse.name,
    keys: Object.keys(orgResponse),
  });
  return transformOrganizationResponse(orgResponse);
}

/**
 * Fetch organization stats. Returns null on 401/404/400 or timeout; throws on other errors.
 * 400 = no org in session yet (e.g. post-checkout).
 */
export async function fetchStatsApi(
  api: ApiClient,
): Promise<OrganizationStats | null> {
  const result = await api.request<OrganizationStats>(STATS_URL, {
    method: "GET",
    timeout: STATS_TIMEOUT_MS,
  });
  if (isApiFailure(result)) {
    if (result.status === 401) {
      logger.debug("Stats fetch skipped - user not authenticated");
      return null;
    }
    if (result.status === 404) {
      logger.debug("Stats endpoint not available");
      return null;
    }
    if (result.status === 400) {
      logger.debug(
        "Stats fetch skipped - no org in session (e.g. post-checkout)",
      );
      return null;
    }
    if (result.code === "TIMEOUT") {
      logger.debug("Stats fetch cancelled or timed out", {
        error: result.message,
      });
      return null;
    }
    if (result.code === "NETWORK_ERROR") {
      logger.debug("Stats fetch network error (expected during initial load)", {
        error: result.message,
      });
      return null;
    }
    logger.error("Failed to fetch stats", { error: result.message });
    throw new Error(
      `Failed to fetch organization stats: ${result.status} ${result.message}`,
    );
  }
  return result.data;
}

function organizationToApiPayload(
  updates: Partial<OrganizationData>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (updates.name !== undefined) payload.name = updates.name;
  if (updates.phone !== undefined) payload.phone = updates.phone;
  if (updates.website !== undefined) payload.website = updates.website;
  if (updates.taxId !== undefined) payload.tax_id = updates.taxId;
  if (updates.addressLine1 !== undefined)
    payload.address_line_1 = updates.addressLine1;
  if (updates.addressLine2 !== undefined)
    payload.address_line_2 = updates.addressLine2;
  if (updates.city !== undefined) payload.city = updates.city;
  if (updates.state !== undefined) payload.state = updates.state;
  if (updates.postalCode !== undefined)
    payload.postal_code = updates.postalCode;
  if (updates.country !== undefined) payload.country = updates.country;
  if (updates.size !== undefined) payload.size = updates.size;
  if (updates.industry !== undefined) payload.industry = updates.industry;
  return payload;
}

/**
 * Update organization. Returns updated OrganizationData; throws on failure.
 */
export async function updateOrganizationApi(
  api: ApiClient,
  updates: Partial<OrganizationData>,
): Promise<OrganizationData> {
  const result = await api.put<OrgApiResponse>(
    "/api/organization",
    organizationToApiPayload(updates),
  );
  if (isApiFailure(result)) {
    logger.error("[ORG API] PUT /api/organization failed", {
      status: result.status,
      detail: result.message,
    });
    throw new Error(
      `Failed to update organization: ${result.status} ${result.message}`,
    );
  }

  const orgResponse = result.data;
  if (!orgResponse || typeof orgResponse !== "object") {
    logger.error("Invalid organization update response", {
      data: result.data,
      orgResponse,
    });
    throw new Error("Invalid organization update response");
  }
  return transformOrganizationResponse(orgResponse);
}

export interface UpdateSettingsResponse {
  settings: Partial<OrganizationSettings>;
  updated_at?: string;
}

/**
 * Update organization settings. Returns updated settings slice; throws on failure.
 */
export async function updateSettingsApi(
  api: ApiClient,
  settings: Partial<OrganizationSettings>,
): Promise<UpdateSettingsResponse> {
  const result = await api.put<{
    settings?: Partial<OrganizationSettings>;
    updated_at?: string;
  }>("/api/organization/settings", { settings });
  if (isApiFailure(result)) throw new Error("Failed to update settings");

  const settingsResponse = result.data;
  if (!settingsResponse || typeof settingsResponse !== "object") {
    logger.error("Invalid organization settings update response", {
      data: result.data,
      settingsResponse,
    });
    throw new Error("Invalid organization settings update response");
  }
  return {
    settings: (settingsResponse.settings ??
      {}) as Partial<OrganizationSettings>,
    updated_at: settingsResponse.updated_at,
  };
}

/**
 * Validate organization slug. Returns { available }; throws on failure.
 */
export async function validateSlugApi(
  api: ApiClient,
  slug: string,
): Promise<{ available: boolean }> {
  const result = await api.post<{ available: boolean }>(
    "/api/organization/validate-slug",
    { slug },
  );
  if (isApiFailure(result)) throw new Error("Failed to validate slug");
  return { available: result.data.available };
}

/**
 * Upload organization logo. Returns { url }; throws on failure.
 */
export async function uploadLogoApi(
  file: File,
  getToken?: ClerkGetToken,
): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("logo", file);
  const headers: Record<string, string> = {};
  const token = await getSupabaseToken(getToken);
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const response = await fetch("/api/organization/logo", {
    method: "POST",
    body: formData,
    headers,
  });
  if (!response.ok) throw new Error("Failed to upload logo");
  const data = await response.json();
  return { url: data.data.url };
}
